import React, { Context, StrictMode } from 'react';
import { EnhancedStore } from '@reduxjs/toolkit';
import { Provider, ReactReduxContextValue } from 'react-redux';
import { HelmetContext, Helmet, Provider as HelmetProvider } from '@okapi/helmet-react';
import { RenderCallback } from '@okapi/core';
import { GlobalRouter, Router } from '@okapi/router-react';
import { RemoteProvider } from '@navi-toolbox/ui-components';
import { buildYMapBundlePath, getLang } from 'utils';
import { App } from 'app';
import { getEnv } from 'common/selectors/env';
import { YandexMetricaScript, GoogleAnalyticsScript } from 'features/analytics';
import { MetaRobots } from '@navi-toolbox/seo';
import { getSeoProperties } from 'features/page-model/ducks/get-model';
import { HrefLangMeta } from '../../features/href-lang-meta';
import { StoreJsonLdMarkup } from 'common/components/store-json-ld/store-json-ld';

export type EntryProps = { router: GlobalRouter; remoteContext?: RenderCallback['remoteContext'] } & (
  | ClientSideEntryProps
  | ServerSideEntryProps
);

type ClientSideEntryProps = {
  helmetContext?: never;
  store: EnhancedStore;
};

type ServerSideEntryProps = {
  helmetContext?: HelmetContext;
  store: EnhancedStore;
};

export function createEntry({ store, router, helmetContext, remoteContext }: EntryProps) {
  return () => {
    const state = store.getState();
    const { ORCHESTRATOR_HOST = '', MAP_API_KEY = '', USE_REMOTES = true } = getEnv(state) || {};
    const orchestratorDomain = ORCHESTRATOR_HOST ? new URL(ORCHESTRATOR_HOST).origin : '';
    const lang = getLang();
    const seoProperties = getSeoProperties(state);
    const context = helmetContext as HelmetContext & Context<ReactReduxContextValue>;

    return (
      <Router router={router}>
        <StrictMode>
          <HelmetProvider context={helmetContext}>
            <Provider store={store}>
              <RemoteProvider active={USE_REMOTES} context={remoteContext}>
                <App />
                <StoreJsonLdMarkup context={context} />
                <MetaRobots content={seoProperties?.metaRobotsContent || []} context={context} />
                <HrefLangMeta context={context} />
                <Helmet>
                  <html lang={lang} />
                  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=2" />
                  <meta content="#444b5b" name="theme-color" />
                  <link href="//fonts.googleapis.com" rel="dns-prefetch" />
                  <link href="https://cdn.lemanapro.ru/" crossOrigin="anonymous" rel="preconnect" />
                  <link href="https://www.googletagmanager.com" crossOrigin="anonymous" rel="preconnect" />
                  <link href="https://www.google-analytics.com" crossOrigin="anonymous" rel="preconnect" />
                  <link href="https://fonts.gstatic.com" crossOrigin="anonymous" rel="preconnect" />
                  <link
                    rel="preload"
                    href="https://static.lemanapro.ru/fronton/lm-main/demi-bold.woff2"
                    as="font"
                    crossOrigin="anonymous"
                  />
                  <link
                    rel="preload"
                    href="https://static.lemanapro.ru/fronton/lm-main/regular.woff2"
                    as="font"
                    crossOrigin="anonymous"
                  />
                  <link
                    href="https://static.lemanapro.ru/fronton/lm-fonts.css"
                    rel="preload"
                    as="style"
                    crossOrigin="anonymous"
                    onLoad={"this.rel = 'stylesheet'" as never}
                  />
                  <link href={orchestratorDomain} crossOrigin="anonymous" rel="preconnect" />
                  <script src={buildYMapBundlePath(MAP_API_KEY)} type="text/javascript" />
                </Helmet>
                <YandexMetricaScript />
                <GoogleAnalyticsScript />
              </RemoteProvider>
            </Provider>
          </HelmetProvider>
        </StrictMode>
      </Router>
    );
  };
}
